/**
 * useViewMode — localStorage-persisted view mode hook
 *
 * Matches the existing localStorage patterns:
 * - `ferp-columns-${primitiveType}` (column visibility)
 * - `ferp:entity-detail:dock` (panel dock position)
 *
 * Usage:
 *   const { viewMode, setViewMode } = useViewMode('actor');
 *   // localStorage key: ferp-view-mode-actor
 */

import { useState, useCallback, useEffect } from 'react';

// DRR-178: Extended with kanban + spatial for dimension-driven rendering
// DRR-183: Added 'ambient' for full-screen cycling display mode
export type DisplayViewMode = 'table' | 'card' | 'master-detail' | 'kanban' | 'spatial' | 'timeline' | 'ambient';

const STORAGE_PREFIX = 'ferp-view-mode-';

const VALID_MODES: ReadonlySet<string> = new Set([
  'table', 'card', 'master-detail', 'kanban', 'spatial', 'ambient',
]);

function isValidMode(value: unknown): value is DisplayViewMode {
  return typeof value === 'string' && VALID_MODES.has(value);
}

export interface UseViewModeReturn {
  viewMode: DisplayViewMode;
  setViewMode: (mode: DisplayViewMode) => void;
}

export function useViewMode(
  storageKey: string,
  defaultMode: DisplayViewMode = 'table',
): UseViewModeReturn {
  const fullKey = `${STORAGE_PREFIX}${storageKey}`;

  const [viewMode, setViewModeState] = useState<DisplayViewMode>(() => {
    try {
      const stored = localStorage.getItem(fullKey);
      if (stored && isValidMode(stored)) return stored;
    } catch {
      // localStorage unavailable — fall back
    }
    return defaultMode;
  });

  const setViewMode = useCallback(
    (mode: DisplayViewMode) => {
      setViewModeState(mode);
      try {
        localStorage.setItem(fullKey, mode);
      } catch {
        // localStorage full or unavailable
      }
    },
    [fullKey],
  );

  // Sync across tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === fullKey && e.newValue && isValidMode(e.newValue)) {
        setViewModeState(e.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [fullKey]);

  return { viewMode, setViewMode };
}
